import type { RowLocalId } from "../ids/row-local-id.ts";

/**
 * Opaque handle to where a calendar event originates.
 *
 * An event's origin is always row-form — even an external /
 * iCalendar-backed event resolves to a backing sheet row. But the
 * backing row id is **captured from the actual source row**, never
 * derived from `(authId, calendarId)`: different origins (per-calendar
 * Google rows, computed rows, external-provider-backed rows) have
 * arbitrary, non-uniform row keys, so the id the source actually has
 * must be carried, not reconstructed.
 *
 * The type is intentionally opaque so future origin metadata (provider
 * event id, recurrence master, …) can be added behind it without
 * changing the carrying contract. Today there is a single shape: a
 * backing row whose `RowLocalId` is the write target.
 */
export class EventOrigin {
  readonly #rowId: RowLocalId;
  private constructor(rowId: RowLocalId) {
    this.#rowId = rowId;
  }

  /** Origin backed by a sheet row whose id is carried verbatim. */
  static backingRow(rowId: RowLocalId): EventOrigin {
    return new EventOrigin(rowId);
  }

  /**
   * The source row that owns this event's cells — the write target.
   * Always present today (origin is always row-form).
   */
  backingRowId(): RowLocalId {
    return this.#rowId;
  }

  equals(other: EventOrigin): boolean {
    return this.#rowId.toString() === other.#rowId.toString();
  }

  toString(): string {
    return this.#rowId.toString();
  }
}
